import { useRef, useState } from 'react'
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core'
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Loader2, Trash2, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/use-toast'
import { resizeImageToDataUrl } from '@/lib/imageUpload'
import { MAX_STOREFRONT_IMAGES, newImageId, type StorefrontImage } from './storefrontConfig'

const MAX_FILE_BYTES = 5 * 1024 * 1024

interface Props {
  images: StorefrontImage[]
  onChange: (next: StorefrontImage[]) => void
  /** The pre-library single banner (storefront_hero_url), if any. */
  legacyHero: string
  onMoveLegacyIntoLibrary: () => void
}

/**
 * Up to 8 images the storefront reuses: the first one is the hero photo and
 * the link-unfurl image; the gallery section shows all of them. Stored inline
 * as data URIs in one setting; the backend serves them as cacheable bytes.
 */
export function ImageLibrary({ images, onChange, legacyHero, onMoveLegacyIntoLibrary }: Props) {
  const { toast } = useToast()
  const fileRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [url, setUrl] = useState('')
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

  const full = images.length >= MAX_STOREFRONT_IMAGES

  const addFiles = async (files: FileList | null) => {
    if (fileRef.current) fileRef.current.value = ''
    if (!files || files.length === 0) return
    const room = MAX_STOREFRONT_IMAGES - images.length
    const picked = Array.from(files).slice(0, room)
    if (picked.length < files.length) {
      toast({ title: `Only ${room} more image${room === 1 ? '' : 's'} fit in the library` })
    }
    setBusy(true)
    const next = [...images]
    try {
      for (const f of picked) {
        if (!f.type.startsWith('image/')) {
          toast({ title: 'Not an image', description: `${f.name} was skipped.`, variant: 'destructive' })
          continue
        }
        if (f.size > MAX_FILE_BYTES) {
          toast({ title: 'Image too large', description: `${f.name} is over 5 MB and was skipped.`, variant: 'destructive' })
          continue
        }
        next.push({ id: newImageId(), src: await resizeImageToDataUrl(f, 1600, 0.82), caption: '' })
      }
      onChange(next)
    } catch (err) {
      toast({ title: 'Could not process image', description: err instanceof Error ? err.message : String(err), variant: 'destructive' })
    } finally {
      setBusy(false)
    }
  }

  const addUrl = () => {
    const u = url.trim()
    if (!/^https?:\/\//i.test(u)) {
      toast({ title: 'Enter a full image link', description: 'It should start with https://', variant: 'destructive' })
      return
    }
    onChange([...images, { id: newImageId(), src: u, caption: '' }])
    setUrl('')
  }

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e
    if (!over || active.id === over.id) return
    const from = images.findIndex((i) => i.id === active.id)
    const to = images.findIndex((i) => i.id === over.id)
    if (from < 0 || to < 0) return
    onChange(arrayMove(images, from, to))
  }

  return (
    <div className="space-y-3">
      {images.length === 0 && legacyHero.trim() !== '' && (
        <div className="flex items-center gap-3 rounded-md border border-dashed p-3">
          <img src={legacyHero.trim()} alt="" className="h-14 w-20 rounded-md border object-cover" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">Current banner</p>
            <p className="text-xs text-muted-foreground">Set before the library existed. Move it in to use it in every hero style.</p>
          </div>
          <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={onMoveLegacyIntoLibrary}>
            Move into library
          </Button>
        </div>
      )}

      {images.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={images.map((i) => i.id)} strategy={rectSortingStrategy}>
            <div className="grid gap-2">
              {images.map((im, idx) => (
                <SortableImage
                  key={im.id}
                  image={im}
                  index={idx}
                  onCaption={(c) => onChange(images.map((x) => (x.id === im.id ? { ...x, caption: c } : x)))}
                  onRemove={() => onChange(images.filter((x) => x.id !== im.id))}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <input
        ref={fileRef}
        type="file"
        multiple
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => addFiles(e.target.files)}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" className="gap-1.5" disabled={busy || full} onClick={() => fileRef.current?.click()}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          Upload images
        </Button>
        <Input
          type="url"
          inputMode="url"
          placeholder="or paste an image link"
          value={url}
          disabled={full}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              addUrl()
            }
          }}
          className="min-w-0 flex-1 basis-40"
        />
        <Button type="button" variant="ghost" size="sm" className="gap-1.5" disabled={full || url.trim() === ''} onClick={addUrl}>
          Add link
        </Button>
        <span className="text-[11px] tabular-nums text-muted-foreground">
          {images.length}/{MAX_STOREFRONT_IMAGES}
        </span>
      </div>
    </div>
  )
}

function SortableImage({
  image,
  index,
  onCaption,
  onRemove,
}: {
  image: StorefrontImage
  index: number
  onCaption: (c: string) => void
  onRemove: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: image.id })
  const style = { transform: CSS.Transform.toString(transform), transition }
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex gap-3 rounded-md border bg-card p-2 ${isDragging ? 'opacity-60' : ''}`}
    >
      <button
        type="button"
        className="flex shrink-0 cursor-grab touch-none items-center text-muted-foreground"
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="relative h-16 w-24 shrink-0 overflow-hidden rounded-md border bg-muted">
        <img src={image.src} alt="" className="h-full w-full object-cover" />
        {index === 0 && (
          <span className="absolute bottom-1 left-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-white">
            Hero
          </span>
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <Input
          value={image.caption ?? ''}
          maxLength={80}
          placeholder="Caption (optional)"
          onChange={(e) => onCaption(e.target.value)}
          className="h-8 text-xs"
        />
        <div className="flex items-center justify-between">
          <span className="truncate text-[11px] text-muted-foreground">
            {image.src.startsWith('data:') ? 'Uploaded' : 'Linked'}
          </span>
          <Button type="button" variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs" onClick={onRemove}>
            <Trash2 className="h-3.5 w-3.5" />
            Remove
          </Button>
        </div>
      </div>
    </div>
  )
}
